import disabled from 'editor/modifiers/disabled';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type Drumset from 'editor/models/drumset';
import type { Instrument } from 'editor/models/drumset';
import type ProjectManagerService from 'editor/services/project-manager';
import SelectableItem from './selectable-item';
import selected from 'editor/modifiers/selected';
import { tracked } from '@glimmer/tracking';
import type { DrumsetFile } from 'editor/services/project-manager';
import eq from 'editor/helpers/eq';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { action } from '@ember/object';
import Button from './button';

interface InstrumentImpportSignature {
  Element: HTMLDivElement;
  Args: {
    drumset: Drumset;
    // eslint-disable-next-line no-unused-vars
    addInstruments: (instruments: Instrument[]) => void;
  };
  Blocks: {
    // default: [];
  };
}

// Refactor: This shares a lot of code with the Part/BuildAccentWindow component
export default class InstrumentImpport extends Component<InstrumentImpportSignature> {
  @service declare projectManager: ProjectManagerService;

  @tracked selectedDrumset?: DrumsetFile;
  @tracked instrumentsLoading = false;
  @tracked selectedInstruments: Instrument[] = [];

  constructor(owner: unknown, args: InstrumentImpportSignature['Args']) {
    super(owner, args);
  }

  get drumset() {
    return this.args.drumset;
  }

  @action
  async selectDrumset(drumset: DrumsetFile) {
    this.selectedDrumset = drumset;
    this.instrumentsLoading = true;
    await drumset.parse();
    this.instrumentsLoading = false;
  }

  get availableDrumsets() {
    return this.projectManager.drumsets
      .filter((d) => !d.drumset || d.drumset != this.args.drumset)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  get availableInstruments() {
    const drumsetPlayableInstruments =
      this.selectedDrumset?.drumset?.playableInstruments || [];
    return drumsetPlayableInstruments
      .filter(
        (i) =>
          this.selectedInstruments.includes(i) ||
          !this.selectedInstruments.map((i) => i.id).includes(i.id),
      )
      .filter((i) => !this.drumset.playableInstruments.map((i) => i.id).includes(i.id));
  }

  @action
  isSelectedInstrument(instrument: Instrument) {
    return this.selectedInstruments.includes(instrument);
  }

  @action
  toggleInstrument(instrument: Instrument, ev?: MouseEvent) {
    if (ev?.shiftKey) {
      const firstSelectedInstrument = this.selectedInstruments.at(0);
      const lastSelectedInstrument = this.selectedInstruments.at(-1);
      if (firstSelectedInstrument && lastSelectedInstrument) {
        const firstIndex = this.availableInstruments.indexOf(firstSelectedInstrument);
        const lastIndex = this.availableInstruments.indexOf(lastSelectedInstrument);
        const currentIndex = this.availableInstruments.indexOf(instrument);
        if (currentIndex < firstIndex) {
          this.selectedInstruments = [
            ...this.selectedInstruments,
            ...this.availableInstruments.slice(currentIndex, firstIndex + 1),
          ];
        } else if (currentIndex > lastIndex) {
          this.selectedInstruments = [
            ...this.selectedInstruments,
            ...this.availableInstruments.slice(lastIndex, currentIndex + 1),
          ];
        } else {
          this.selectedInstruments = [
            ...this.selectedInstruments,
            ...this.availableInstruments.slice(firstIndex, currentIndex + 1),
          ];
        }
      } else {
        this.selectedInstruments = [instrument];
      }
    } else {
      if (this.isSelectedInstrument(instrument)) {
        this.selectedInstruments = this.selectedInstruments.filter(
          (i) => i != instrument,
        );
      } else {
        this.selectedInstruments = [...this.selectedInstruments, instrument];
      }
    }
    // remove window selection
    window.getSelection()?.removeAllRanges();
  }

  <template>
    <div
      class="flex flex-col h-full min-h-0 gap-2 overflow-auto dark:bg-primary-darkComponent dark:text-white"
      data-test-instrument-import
      ...attributes
    >
      <h1>Import instruments</h1>
      <div class="flex gap-2 min-h-0 overflow-auto h-full">
        <div class="overflow-auto border-r dark:border-primary-darkBorderColor">
          <div data-test-drumsets class="flex flex-col gap-px h-max">
            {{#each this.availableDrumsets as |drumset|}}
              <SelectableItem
                @icon="drum"
                @model={{drumset}}
                @field="name"
                @when={{false}}
                title={{drumset.name}}
                {{selected when=(eq this.selectedDrumset drumset)}}
                {{on "click" (fn this.selectDrumset drumset)}}
              >{{drumset.name}}</SelectableItem>
            {{/each}}
          </div>
        </div>
        <div class="overflow-auto">
          <div data-test-instruments class="flex flex-col gap-px h-max">
            {{#if this.instrumentsLoading}}
              <div>Loading...</div>
            {{else}}
              {{#each this.availableInstruments as |instrument|}}
                <SelectableItem
                  @model={{instrument}}
                  @field="name"
                  @when={{false}}
                  title={{instrument.name}}
                  {{selected when=(this.isSelectedInstrument instrument)}}
                  {{on "click" (fn this.toggleInstrument instrument)}}
                >{{instrument.id}}. {{instrument.name}}</SelectableItem>

              {{else}}
                No available instruments
              {{/each}}
            {{/if}}
          </div>
        </div>
      </div>
      <div class="text-right">
        <Button
          class="text-white bg-green-600 hover:bg-green-700"
          {{disabled when=(eq this.selectedInstruments.length 0)}}
          {{on "click" (fn @addInstruments this.selectedInstruments)}}
        >Import instruments</Button>
      </div>
    </div>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    InstrumentImpport: typeof InstrumentImpport;
  }
}
